import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { IObservableArray } from 'mobx';
import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText, withLocalization } from 'bernie-l10n';
import { ChildModel } from 'stores/models/child-model';
import { RoomConfig, RoomPickerModel } from 'components/common/room-picker/room-picker-model';
import { RoomModel } from 'stores/models/room-model';
import { UitkFauxInput, UitkTextInput } from 'uitk-react-input';
import { FormFieldBinding } from './form-field-binding';
import { RoomPicker } from 'components/common/room-picker/room-picker';
import { SearchFormFieldProps } from 'components/common/search-form';
import { AlterLocalizationProvider } from 'bernie-alter';

export interface TravelersProps extends LocalizedComponentProps, SearchFormFieldProps {
  analytics?: AnalyticsStore;
  buttonText?: string;
  dismissOnSave?: boolean;
  hidden?: boolean;
  id?: string;
  name?: string;
  onChange?: any;
  placeholder?: string;
  routeStore?: RouteStore;
  hideRoomsInDisplaySummary?: boolean;
}

export interface TravelersState {
  adults: string;
  children: string;
  mounted: boolean;
  value: string;
}

@withLocalization
@FormFieldBinding
@inject('analytics', 'routeStore')
@observer
export class Travelers extends React.Component<TravelersProps, TravelersState> {
  public focusButton: any = React.createRef();
  private roomPickerModel: RoomPickerModel;
  constructor(props) {
    super(props);
    const guestCount = this.props.model && this.props.model.guestCount && this.props.model.guestCount.toString();

    this.state = {
      adults: guestCount,
      children: '',
      mounted: false,
      value: ''
    };

    this.roomPickerModel = new RoomPickerModel({ adults: '', children: '' }, props.l10n);

    this.convertRoomConfigToRoomModel = this.convertRoomConfigToRoomModel.bind(this);
    this.convertRoomModelToRoomConfig = this.convertRoomModelToRoomConfig.bind(this);
    this.dismiss = this.dismiss.bind(this);
    this.open = this.open.bind(this);
    this.openDatepicker = this.openDatepicker.bind(this);
    this.save = this.save.bind(this);
    this.validate = this.validate.bind(this);
    this.parseRooms = this.parseRooms.bind(this);
    props.registerValidator(() => this.validate('children'));
    props.registerValidator(() => this.validate('adults'));
  }

  public static defaultProps = {
    dismissOnSave: true,
    l10n: {}
  };

  private open() {
    const { analytics, routeStore } = this.props;

    routeStore.updateSearch({ pwaDialog: 'travelerPicks' });

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':ChangeRoomGuest', 'Open Room Picker', analytics.omnitureData);
    }
  }

  private openDatepicker() {
    const { analytics, routeStore } = this.props;

    routeStore.updateSearch({ pwaDialog: 'datepicker-start' });

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':OpenDatepickerRoomGuest', 'Open Datepicker from Room Guest', analytics.omnitureData);
    }
  }

  private save(adults: string, children: string, value: string) {
    const { dismissOnSave, updateModel } = this.props;
    const isDateless = this.props.model.isDateless;

    if (dismissOnSave && !isDateless) {
      this.dismiss();
    }

    this.roomPickerModel.setupRooms(adults, children);
    const { finalProps, rooms } = this.roomPickerModel;

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name: 'rooms',
          value: this.convertRoomConfigToRoomModel(rooms)
        }
      });
    }

    this.setState({
      adults: finalProps.adults,
      children: finalProps.children,
      value
    });

    if (isDateless) {
      this.openDatepicker();
    }
  }

  private dismiss() {
    this.props.routeStore.goBack();
    // manage focus back to the trigger
    const focusButton = this.focusButton;
    if (focusButton.current && typeof focusButton.current.focus === 'function') {
      focusButton.current.focus();
    }
  }

  private convertRoomModelToRoomConfig(rooms: RoomModel[]): IObservableArray<RoomConfig> {
    return rooms.map((room: RoomModel) => ({
      adults: room.adults,
      children: room.children.map((child: ChildModel) => child.age)
    })) as IObservableArray<RoomConfig>;
  }

  private convertRoomConfigToRoomModel(rooms: IObservableArray<RoomConfig>): RoomModel[] {
    return rooms.map(
      (room: RoomConfig) =>
        new RoomModel({
          adults: room.adults,
          children: room.children.map((child: number) => new ChildModel({ age: child }))
        })
    );
  }

  private parseRooms(rooms) {
    if (!rooms) {
      return;
    }

    this.roomPickerModel.rooms = this.convertRoomModelToRoomConfig(rooms);
    const { displaySummary, finalProps } = this.roomPickerModel;

    return {
      adults: finalProps.adults,
      children: finalProps.children,
      value: displaySummary
    };
  }

  public componentDidMount() {
    const { rooms } = this.props.model;

    this.setState({
      ...this.parseRooms(rooms),
      mounted: true
    });
  }

  public componentWillReceiveProps(nextProps) {
    const { adults, children } = this.props.model;
    const { model } = nextProps;
    this.setState({
      ...this.parseRooms(model.rooms)
    });
  }

  public render() {
    const { id, name, placeholder, updateModel, hidden, wrapperClassNames, l10n } = this.props;

    const { adults, mounted, value } = this.state;

    const idBtn = `${id}__btn`;
    // If mounted, use summary, otherwise just use adults=
    const renderedValue = mounted ? value : adults;
    const buttonText = this.props.model.isDateless
      ? l10n.formatText('hotels.infosite.roomSelection.dateless.ctabutton.label')
      : this.props.buttonText;

    const renderLabel = () => (
      <span>
        <LocalizedText message="hotels.search.form.travelers.label" />
        <span className="is-visually-hidden">{renderedValue}</span>
      </span>
    );

    const classNames: string[] = [];

    if (hidden) {
      return this.fieldValues(this.state);
    }

    if (mounted) {
      classNames.push('is-hidden');
    }

    return (
      <div className={wrapperClassNames || 'uitk-cell s-cell-1-1 m-cell-1-4 l-cell-1-6 xl-cell-1-6'}>
        <UitkTextInput
          id={id}
          name={mounted ? name : 'adults'}
          className={classNames.join(' ')}
          label={renderLabel()}
          placeholder={placeholder}
          value={renderedValue}
          onChange={updateModel}
          data-stid="input-date"
          icon="person"
          invalid={mounted && (this.validate('adults') || this.validate('children'))}
        >
          {mounted && (
            <UitkFauxInput domRef={this.focusButton} id={idBtn} type="button" data-stid="open-room-picker" onClick={this.open}>
              <span className="is-visually-hidden">
                <LocalizedText message="hotels.search.form.travelers.label" />{' '}
              </span>
              {renderedValue}
            </UitkFauxInput>
          )}
        </UitkTextInput>
        {mounted && this.fieldValues(this.state)}
        <QueryRoute query={{ pwaDialog: 'travelerPicks' }}>
          <Layer id="home-travelerpicks" onDismiss={this.dismiss}>
            <AlterLocalizationProvider>
              <RoomPicker
                adultsString={this.state.adults}
                childrenString={this.state.children}
                onSave={this.save}
                onDismiss={this.dismiss}
                analytics={this.props.analytics}
                buttonText={buttonText}
              />
            </AlterLocalizationProvider>
          </Layer>
        </QueryRoute>
      </div>
    );
  }

  private fieldValues = ({ adults, children }) => (
    <React.Fragment>
      {adults && <input type="hidden" name="adults" value={adults} readOnly={true} />}
      {children && <input type="hidden" name="children" value={children} readOnly={true} />}
    </React.Fragment>
    // tslint:disable-next-line:semicolon
  );

  private validate(key) {
    const { l10n } = this.props;
    const { adults, children } = this.state;
    const { formatText } = l10n;

    if (key === 'adults') {
      if (!adults) {
        return formatText('hotels.search.validation.invalidGuests.text');
      }
      const zeroOldAdult = adults.split(',').some(adult => parseInt(adult, 10) <= 0);
      if (zeroOldAdult) {
        return formatText('hotels.search.validation.invalidGuests.text');
      }
    }

    if (key === 'children') {
      if (!children) {
        return;
      }

      const childAges = children.split(',');
      const message =
        childAges.length === 1
          ? formatText('hotels.search.validation.missingChildAge.text')
          : formatText('hotels.search.validation.missingChildrenAges.text');

      let error = false;

      childAges.forEach(child => {
        if (!child.match(/\d{1,2}_\d{1,2}/)) {
          // i.e. roomNumber_childAge, e.g. 1_4
          error = true;
        }
      });

      return error ? message : undefined;
    }
  }
}
